import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Search from 'classes/Recipe/Search/Search';
import Calories from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Calories';
import Density from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Density';
import Picture from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Picture';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import IconComponent from 'components/desktop/_general/Icon/IconComponent';
import IconCo2Round from 'components/desktop/_general/IconCo2/IconCo2Round';
import NutriScoreDiagram from 'components/desktop/_general/NutriScoreDiagram/NutriScoreDiagram';
import SeasonalityBar from 'components/desktop/_general/SeasonalityBar/SeasonalityBar';
import { SearchLogic } from 'enums/searchLogic';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import usePreferredAllergens from 'hooks/redux/usePreferredAllergens';
import { usePermissions } from 'hooks/usePermissions';
import { IExclusionLogicSelectItem } from 'interfaces/IExclusionLogicSelectItem';
import * as Icon from 'types/Icon/Icon';
import * as Ingredient from 'types/Ingredient/Search/Ingredient';

interface IProps {
	handleArchiveIngredient: (input: string) => Promise<void>;
	ingredient: Ingredient.Type;
}

const SearchResultListRow = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isRecipeReader } = usePermissions();
	const { isIngredientEditor } = usePermissions();
	const preferredAllergens = usePreferredAllergens();

	const allergensUserSet = useMemo(
		() => preferredAllergens.toSet((x) => `${x.id}_${x.logic}`),
		[preferredAllergens]
	);

	const onRecipeLinkClick = async (id: string, name: string, e: any) => {
		e.preventDefault();
		const recipeAdvancedSearch = new Search();
		recipeAdvancedSearch.selectIngredient.saved = [
			{ id: id, name: name, logic: SearchLogic.Include } as IExclusionLogicSelectItem,
		];
		const url: string | null = recipeAdvancedSearch.mapToUrl();
		if (url) navigate(`/recipe/search/ingredient/${url}`);
	};

	const handleIngredientDetailClick = (): void => {
		navigate(`/ingredient/detail/${props.ingredient.id}`);
	};

	const getButtonDataEdit = (): BtnData | undefined => {
		if (isIngredientEditor) {
			return {
				onClick: handleIngredientDetailClick,
			};
		}
	};

	const handleCopyIngredient = (id: string): (() => void) => {
		return () => {
			navigate(`/ingredient/copy/${id}`);
		};
	};

	const getButtonDataArchive = (): BtnData | undefined => {
		if (isIngredientEditor) {
			return {
				onClick: () => props.handleArchiveIngredient(props.ingredient.id),
			};
		}
	};

	const getButtonDataView = (): BtnData | undefined => {
		if (!isIngredientEditor) {
			return {
				onClick: handleIngredientDetailClick,
			};
		}
	};

	const getButtonDataCopy = (id: string): BtnData | undefined => {
		if (!isIngredientEditor) return;
		return {
			onClick: handleCopyIngredient(id),
		};
	};

	const renderRecipeLink = (): JSX.Element => {
		if (props.ingredient.countOfRecipes > 0) {
			if (isRecipeReader) {
				return (
					<span
						className="primary"
						onClick={(e: any) =>
							onRecipeLinkClick(
								props.ingredient.id,
								props.ingredient.nameSingularTranslation,
								e
							)
						}
						style={{ zIndex: 100 }}
					>
						{`${props.ingredient.countOfRecipes} ${t('_general:RECIPE', {
							count: props.ingredient.countOfRecipes,
						})}`}
					</span>
				);
			} else {
				return (
					<span className="primary">
						{`${props.ingredient.countOfRecipes} ${t('_general:RECIPE', {
							count: props.ingredient.countOfRecipes,
						})}`}
					</span>
				);
			}
		}
		return <>{`0 ${t('_general:RECIPES', { count: props.ingredient.countOfRecipes })}`}</>;
	};

	const renderButtons = (): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit()}
				archive={getButtonDataArchive()}
				view={getButtonDataView()}
				copy={getButtonDataCopy(props.ingredient.id)}
			/>
		);
	};

	return (
		<Link to={`/ingredient/detail/${props.ingredient.id}`} className="unstyled-link">
			<div className="recipe-table-item">
				<Picture image={props.ingredient.image} />
				<div id="column-first">
					<div id="title">
						<strong>{props.ingredient.nameSingularTranslation}</strong>
					</div>
					<div id="info">
						{renderRecipeLink()}
						<br />
						<Calories
							calories={props.ingredient.calories}
							characteristic={props.ingredient.characteristic}
						/>
						<Density density={props.ingredient.density} />
					</div>
				</div>
				<div id="column-second">
					<div className="icon-column justify-content-start">
						<SeasonalityBar
							seasonalityCode={getSeasonalityCode(props.ingredient.seasons)}
						/>
					</div>
					<div className="row">
						<div className="col-5">
							<span className="grey">{t('_general:LAST_CHANGE_DATE')}:</span>
							<br />
							<span className="grey">{t('_general:LAST_EDITOR')}:</span>
							<br />
						</div>
						<div className="col-7">
							<FormatedDate date={props.ingredient.lastEditedDateUtc} />
							<br />
							<span>{Ingredient.getAuthor(props.ingredient)}</span>
						</div>
					</div>
				</div>
				<div id="column-last">
					{props.ingredient.hasNutriScore && props.ingredient.nutriScore && (
						<div className="justify-content-end">
							<NutriScoreDiagram nutriScore={props.ingredient.nutriScore} />
						</div>
					)}
					<div id="icon-rows">
						{Ingredient.getIcons(props.ingredient, allergensUserSet)
							.slice(0, 4)
							.map((icon: Icon.Type, i: number) => (
								<IconComponent icon={icon} key={i} />
							))}
						<IconCo2Round
							color={
								props.ingredient.carbonDioxideLabel
									? props.ingredient.carbonDioxideLabel.color!
									: ''
							}
							label={
								props.ingredient.carbonDioxideLabel
									? props.ingredient.carbonDioxideLabel.label!
									: ''
							}
						/>
					</div>
					<div>{renderButtons()}</div>
				</div>
			</div>
		</Link>
	);
};

export default SearchResultListRow;
